import React, { useCallback, useImperativeHandle, useRef, type ReactNode } from 'react';
import {
  TEXT_FIELD_APPEARANCE_OUTLINED,
  TEXT_FIELD_SIZE_MD,
  type TextFieldAppearance,
  type TextFieldSize,
} from './TextField.constants';
import {
  Input,
  LeadingSlot,
  TrailingSlot,
  Wrapper,
} from './TextField.styled';

export interface TextFieldProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange' | 'size' | 'children' | 'onFocus'
  > {
  /** Controlled value. */
  value: string;
  /** Called with the new string whenever the user types. */
  onChange: (value: string) => void;
  /**
   * Visual treatment. `outlined` (default) is a tinted surface with a
   * border, used in standalone forms. `naked` strips the chrome and is
   * used inside dense list rows for inline rename — the input visually
   * replaces the static text without a layout shift.
   */
  appearance?: TextFieldAppearance;
  /** Pill height token. `sm` (28px) for toolbars, `md` (32px) for forms. */
  size?: TextFieldSize;
  /** Marks the field as in error — paints the border red. */
  invalid?: boolean;
  /** Custom leading icon — Material symbol span, react-icons element, raw svg. */
  leadingIcon?: ReactNode;
  /** Custom trailing icon — same shape as `leadingIcon`. */
  trailingIcon?: ReactNode;
  /**
   * When true, the field grows to fill its container instead of capping at
   * a sensible 140px minimum. Use inside flex toolbars or full-width forms.
   */
  stretch?: boolean;
  /**
   * Auto-select all of the input's contents the first time it gains focus.
   * Mirrors the inline-rename pattern: clicking into a row's name should
   * select the existing label so the user can immediately overwrite it.
   */
  selectOnFocus?: boolean;
  /**
   * Called when the input gains focus. Fires after the optional
   * {@link selectOnFocus} side-effect.
   */
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  dataTestId?: string;
}

/**
 * Generic single-line text input. The atomic primitive that more opinionated
 * fields ({@link InlineRenameField}, search inputs, validated form fields,
 * …) compose on top of.
 *
 * Theme-agnostic: every colour falls back to a sensible literal so the field
 * renders against any consumer theme. Forwards its ref to the underlying
 * `<input>` so callers can imperatively focus / measure / select.
 *
 * @example
 *   const [name, setName] = useState('');
 *   <TextField value={name} onChange={setName} placeholder="Process name" />
 *
 *   // Dense inline rename — naked appearance, auto-selects on focus.
 *   <TextField
 *     value={draft}
 *     onChange={setDraft}
 *     appearance="naked"
 *     size="sm"
 *     selectOnFocus
 *     stretch
 *   />
 */
const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField(
    {
      value,
      onChange,
      appearance = TEXT_FIELD_APPEARANCE_OUTLINED,
      size = TEXT_FIELD_SIZE_MD,
      invalid = false,
      leadingIcon,
      trailingIcon,
      stretch = false,
      selectOnFocus = false,
      disabled = false,
      onFocus,
      placeholder,
      dataTestId = 'text-field',
      ...rest
    },
    ref,
  ) {
    const innerRef = useRef<HTMLInputElement | null>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLInputElement, []);

    const handleFocus = useCallback<React.FocusEventHandler<HTMLInputElement>>(
      (event) => {
        if (selectOnFocus) {
          // Defer to the next tick so iOS / Safari don't override the
          // selection with their own caret-placement logic when focus
          // arrives via tap.
          requestAnimationFrame(() => {
            innerRef.current?.select();
          });
        }
        onFocus?.(event);
      },
      [selectOnFocus, onFocus],
    );

    return (
      <Wrapper
        $appearance={appearance}
        $size={size}
        $invalid={invalid}
        $disabled={disabled}
        $stretch={stretch}
        data-testid={dataTestId}
        data-appearance={appearance}
        data-size={size}
        data-invalid={invalid || undefined}
      >
        {leadingIcon && (
          <LeadingSlot
            $size={size}
            aria-hidden="true"
            data-testid={`${dataTestId}__leading`}
          >
            {leadingIcon}
          </LeadingSlot>
        )}
        <Input
          ref={innerRef}
          $size={size}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          data-testid={`${dataTestId}__input`}
          {...rest}
        />
        {trailingIcon && (
          <TrailingSlot
            $size={size}
            aria-hidden="true"
            data-testid={`${dataTestId}__trailing`}
          >
            {trailingIcon}
          </TrailingSlot>
        )}
      </Wrapper>
    );
  },
);

export default TextField;
